import { useMutation } from '@apollo/client/react';
import { type BasicCouponFragment, ValidateCouponDocument } from '@drycut/graphql';
import {
  ActionButtons,
  Button,
  FormHelpText,
  LoadingBar,
  Stack,
  TextField,
} from '@drycut/ui-components';
import { useEffect, useRef } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';

type Props = {
  onCancel: () => void;
  onValidate: (data: BasicCouponFragment) => void;
};

export default function CouponForm({ onCancel, onValidate }: Props): React.ReactElement {
  const { setError, clearErrors } = useFormContext();
  const phoneNumber = useWatch({ name: 'phone' });
  const couponCode = useWatch({ name: 'couponCode' });
  const inputRef = useRef<HTMLInputElement>(null);

  const [validateCoupon, { loading }] = useMutation(ValidateCouponDocument);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [inputRef]);

  const validateCode = async () => {
    clearErrors('couponCode');

    const { data } = await validateCoupon({
      variables: {
        input: {
          phoneNumber,
          code: couponCode,
        },
      },
    });

    const validatedCoupon = data?.validateCoupon?.coupon;
    const errors = data?.validateCoupon?.errors || [];

    if (validatedCoupon) {
      onValidate(validatedCoupon);
    } else {
      setError('couponCode', { message: errors[0].message, type: 'manual' }, { shouldFocus: true });
    }
  };

  return (
    <>
      <Controller
        name="couponCode"
        render={({ field, fieldState: { invalid, error } }) => (
          <Stack className="text-left">
            <TextField
              {...field}
              label="Gift Card Code"
              className="text-left"
              autoComplete="off"
              ref={inputRef}
              invalid={invalid}
              disabled={loading}
            />
            <LoadingBar loading={loading} />
            <FormHelpText error={invalid}>{error?.message}</FormHelpText>
          </Stack>
        )}
      />
      <ActionButtons>
        <Button
          type="button"
          color="primary"
          onClick={validateCode}
          disabled={loading || couponCode.length < 3}
        >
          Einlösen
        </Button>

        <Button onClick={onCancel} fill="outlined">
          Cancel
        </Button>
      </ActionButtons>
    </>
  );
}
